import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { FaArrowLeft, FaPlus, FaTrash, FaUsersCog } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";

export default function SchoolAdminDetails() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [schoolName, setSchoolName] = useState("School Admins");
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({ school_id: schoolId, name: "", mobile: "", password: "", role: "admin" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const [adminsResponse, schoolsResponse] = await Promise.all([
        api.get(`/super_admin/school_admin/list?school_id=${schoolId}`),
        api.get("/super_admin/school/list"),
      ]);
      setAdmins(adminsResponse.data?.data || []);
      const school = (schoolsResponse.data?.data || []).find((item) => String(item.id) === String(schoolId));
      setSchoolName(school?.name || "School Admins");
    } catch (err) {
      console.error("Fetch school admins error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [schoolId]);

  const handleOpen = () => {
    setFormData({ school_id: schoolId, name: "", mobile: "", password: "", role: "admin" });
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !/^\d{10}$/.test(formData.mobile) || formData.password.length < 6) {
      setSnackbar({ open: true, message: "Enter a name, valid 10-digit mobile, and password of at least 6 characters.", severity: "warning" });
      return;
    }
    try {
      const res = await api.post("/super_admin/school_admin/add", formData, { headers: { "Content-Type": "application/json" } });
      setSnackbar({ open: true, message: res.data?.message || "Admin saved", severity: res.data?.status ? "success" : "error" });
      if (res.data?.status) {
        setOpenDialog(false);
        fetchDetails();
      }
    } catch (err) {
      console.error("Save admin error:", err);
      setSnackbar({ open: true, message: "Something went wrong", severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Admin?")) return;
    try {
      const res = await api.post("/super_admin/school_admin/delete", { id }, { headers: { "Content-Type": "application/json" } });
      setSnackbar({ open: true, message: res.data?.message || "Admin deleted", severity: res.data?.status ? "success" : "error" });
      if (res.data?.status) fetchDetails();
    } catch (err) {
      console.error("Delete admin error:", err);
      setSnackbar({ open: true, message: "Something went wrong", severity: "error" });
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, boxShadow: "0 16px 40px rgba(16,42,67,0.1)", border: "1px solid rgba(78,102,135,0.14)", background: "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(239,250,250,0.92))" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
          <Box sx={{ width: 44, height: 44, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 1.5, color: "#fff", background: "linear-gradient(135deg, #2563eb, #0891b2)" }}>
            <FaUsersCog size={23} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" fontWeight={900} sx={{ color: "#102a43", overflowWrap: "anywhere" }}>{schoolName}</Typography>
            <Typography variant="body2" sx={{ color: "#607086", mt: 0.35, fontWeight: 600 }}>Administrators and staff accounts</Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button variant="outlined" startIcon={<FaArrowLeft />} onClick={() => navigate("/super_admin/schooladmins")} sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800 }}>Back</Button>
          <Button variant="contained" startIcon={<FaPlus />} onClick={handleOpen} sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800, background: "linear-gradient(135deg, #2563eb, #0891b2)" }}>Add Admin</Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: "#0891b2" }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: "auto", boxShadow: "0 14px 34px rgba(16,42,67,0.1)", border: "1px solid rgba(78,102,135,0.14)" }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead sx={{ bgcolor: "#123a5a" }}>
              <TableRow>{["ID", "Name", "Mobile", "Role", "Created At", "Actions"].map((heading) => <TableCell key={heading} align={heading === "Actions" ? "right" : "left"} sx={{ color: "#fff", fontWeight: 800, whiteSpace: "nowrap", borderBottom: 0 }}>{heading}</TableCell>)}</TableRow>
            </TableHead>
            <TableBody>
              {admins.map((admin, index) => (
                <TableRow key={admin.id} hover sx={{ backgroundColor: index % 2 ? "#f7fafc" : "#fff", "& td": { borderColor: "rgba(78,102,135,0.12)" } }}>
                  <TableCell sx={{ fontWeight: 800, color: "#102a43" }}>{admin.id}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{admin.name}</TableCell>
                  <TableCell>{admin.mobile}</TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>{admin.role}</TableCell>
                  <TableCell>{admin.created_at}</TableCell>
                  <TableCell align="right"><Button size="small" color="error" startIcon={<FaTrash />} onClick={() => handleDelete(admin.id)} sx={{ textTransform: "none", fontWeight: 800 }}>Delete</Button></TableCell>
                </TableRow>
              ))}
              {!admins.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: "#607086", fontWeight: 700 }}>No admins found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add School Admin</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Name" fullWidth value={formData.name} onChange={(e) => setFormData((current) => ({ ...current, name: e.target.value }))} />
          <TextField margin="dense" label="Mobile" fullWidth value={formData.mobile} inputProps={{ maxLength: 10, inputMode: "numeric" }} onChange={(e) => setFormData((current) => ({ ...current, mobile: e.target.value.replace(/\D/g, "") }))} />
          <TextField margin="dense" label="Password" type="password" fullWidth value={formData.password} onChange={(e) => setFormData((current) => ({ ...current, password: e.target.value }))} />
          <TextField margin="dense" select label="Role" fullWidth value={formData.role} onChange={(e) => setFormData((current) => ({ ...current, role: e.target.value }))}>
            <MenuItem value="super_admin">Super Admin</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenDialog(false)}>Cancel</Button><Button onClick={handleSave} variant="contained">Save</Button></DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={2500} onClose={() => setSnackbar((current) => ({ ...current, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Paper>
  );
}
